/**
 * Safe Storage Wrapper
 *
 * Provides defensive localStorage/sessionStorage access with graceful fallbacks.
 * Handles mobile browser restrictions, private mode, and disabled storage.
 *
 * Why needed:
 * - Mobile browsers throw exceptions when storage is unavailable
 * - Private/Incognito mode blocks storage access
 * - iOS Safari's Intelligent Tracking Prevention
 * - Cross-origin iframe restrictions
 *
 * RULE 15: Proper error handling without suppressions
 */

import { logger } from '@axon/ui/utils';

/**
 * Storage interface matching Web Storage API
 */
interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  readonly length: number;
  key: (index: number) => string | null;
}

/**
 * In-memory fallback storage for when localStorage is unavailable
 * Matches Web Storage API interface
 */
class MemoryStorage implements SafeStorage {
  private data: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  clear(): void {
    this.data.clear();
  }

  get length(): number {
    return this.data.size;
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }
}

/**
 * Test if storage is available and accessible
 * #COMPLETION_DRIVE: Assuming storage test doesn't affect application state
 * #SUGGEST_VERIFY: Test with temporary key that's immediately removed
 */
function testStorage(storage: Storage): boolean {
  try {
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    const result = storage.getItem(testKey) === 'test';
    storage.removeItem(testKey);
    return result;
  } catch (error) {
    logger.debug('Storage test failed', { error });
    return false;
  }
}

/**
 * Get safe storage implementation
 * Returns localStorage if available, otherwise in-memory fallback
 */
function getSafeStorage(storageType: 'local' | 'session'): SafeStorage {
  // Server-side rendering check
  if (typeof window === 'undefined') {
    logger.debug('SSR detected, using memory storage');
    return new MemoryStorage();
  }

  try {
    const storage = storageType === 'local' ? window.localStorage : window.sessionStorage;

    // Test if storage is actually usable (not just defined)
    if (testStorage(storage)) {
      return storage;
    }

    logger.warn(`${storageType}Storage unavailable, using memory fallback`);
    return new MemoryStorage();
  } catch (error) {
    logger.warn(`${storageType}Storage access denied, using memory fallback`, { error });
    return new MemoryStorage();
  }
}

/**
 * Safe localStorage wrapper
 * Automatically falls back to in-memory storage when localStorage is unavailable
 */
export const safeLocalStorage: SafeStorage = getSafeStorage('local');

/**
 * Safe sessionStorage wrapper
 * Automatically falls back to in-memory storage when sessionStorage is unavailable
 */
export const safeSessionStorage: SafeStorage = getSafeStorage('session');

/**
 * Check if actual localStorage is available (not fallback)
 * Useful for showing warnings to users about storage limitations
 */
export function isRealLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return testStorage(window.localStorage);
  } catch {
    return false;
  }
}

/**
 * Check if actual sessionStorage is available (not fallback)
 */
export function isRealSessionStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return testStorage(window.sessionStorage);
  } catch {
    return false;
  }
}

/**
 * Get storage type description for user-facing messages
 */
export function getStorageStatus(): {
  localStorage: 'available' | 'memory-fallback' | 'unavailable';
  sessionStorage: 'available' | 'memory-fallback' | 'unavailable';
} {
  return {
    localStorage: isRealLocalStorageAvailable() ? 'available' : 'memory-fallback',
    sessionStorage: isRealSessionStorageAvailable() ? 'available' : 'memory-fallback',
  };
}

/**
 * Safe JSON parse with fallback
 * Returns null on parse error instead of throwing
 */
export function safeJsonParse<T>(value: string | null, fallback: T | null = null): T | null {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    logger.error('JSON parse failed', { error, value: value.substring(0, 100) });
    return fallback;
  }
}

/**
 * Safe JSON stringify with fallback
 * Returns empty string on stringify error instead of throwing
 */
export function safeJsonStringify<T>(value: T, fallback: string = '{}'): string {
  try {
    return JSON.stringify(value);
  } catch (error) {
    logger.error('JSON stringify failed', { error });
    return fallback;
  }
}
